/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExpandableCard, ExpandableCardProps } from 'src/core/components/ExpandableCard';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logDTButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { TranslationFileSourceOptions } from 'src/translation/components/TranslationCreationPage';

type TranslationQuotaConsumptionCardProps = {
    filesNumberQuota?: number;
    filesSizeQuota?: number;
    selectedFilesNumber?: number;
    selectedFilesSize?: number;
    isFileMaxSizeExceeded: boolean;
    disableNext: (nextDisabled: boolean) => void;
    selectedFilesLocation: TranslationFileSourceOptions;
};

const messages = defineMessages({
    title: {
        id: 'translation.translationQuotaConsumptionCard.title',
        defaultMessage: `You have selected {selectedFilesNumber} out of maximum {filesNumberQuota} files and {selectedFilesSize} MB out of maximum {filesSizeQuota} MB. Files may not be larger than 40 MB"`
    },
    quotaMessage: {
        id: 'translation.translationQuotaConsumptionCard.quotaMessage',
        defaultMessage: `Each translation job is limited to {filesNumberQuota} files or {filesSizeQuota} MB of content. Individual files may not be larger than {maxFileSize} MB.`
    },
    selectedFilesNumberQuotaMessage: {
        id: 'translation.translationQuotaConsumptionCard.selectedFilesNumberQuotaMessage',
        defaultMessage: `Total number of files`
    },
    selectedFilesSizeQuotaMessage: {
        id: 'translation.translationQuotaConsumptionCard.selectedFilesSizeQuotaMessage',
        defaultMessage: `Total files size`
    },
    selectedQuotaDetailsMessage: {
        id: 'translation.translationQuotaConsumptionCard.selectedQuotaDetailsMessage',
        defaultMessage: `{selectedQuota} of {totalQuota}`
    },
    selectedFilesSizeQuotaDetailsMessage: {
        id: 'translation.translationQuotaConsumptionCard.selectedFilesSizeQuotaDetailsMessage',
        defaultMessage: `{selectedFilesSize} of {filesSizeQuota} MB`
    },
    updatingNumbersSectionMessage: {
        id: 'translation.translationQuotaConsumptionCard.updatingNumbersSectionMessage',
        defaultMessage: `Number of files selected`
    },
    outOfFilesNumberQuota: {
        id: 'translation.translationQuotaConsumptionCard.outOfFilesNumberQuota',
        defaultMessage: `You have exceeded your files quota by {remainingFilesNumber} files`
    },
    outOfFilesSizeQuota: {
        id: 'translation.translationQuotaConsumptionCard.outOfFilesSizeQuota',
        defaultMessage: `You have exceeded your size quota by {remainingFilesSize} MB`
    },
    fileExceedMaxSize: {
        id: 'translation.translationQuotaConsumptionCard.fileExceedMaxSize',
        defaultMessage: `You have exceeded your max single file size`
    },
    almostOutOfFilesNumberQuota: {
        id: 'translation.translationQuotaConsumptionCard.almostOutOfFilesNumberQuota',
        defaultMessage: `You are about to hit your files number quota`
    },
    almostOutOfFilesSizeQuota: {
        id: 'translation.translationQuotaConsumptionCard.almostOutOfFilesSizeQuota',
        defaultMessage: `You are about to hit your size quota`
    },
    cardName: {
        id: 'translation.translationQuotaConsumptionCard.cardName',
        defaultMessage: 'Document translation quota consumption card.'
    }
});

const theme = getScopedTheme('TranslationQuotaConsumptionCard');

const expandableCardStyles: ExpandableCardProps['styles'] = {
    headerContainer: { padding: '0 10px 0 0', background: theme.cardBackgroundColor },
    root: { flex: 0 }
};

export const TranslationQuotaConsumptionCard = observer((props: TranslationQuotaConsumptionCardProps) => {
    const {
        filesNumberQuota,
        filesSizeQuota,
        selectedFilesNumber = null,
        selectedFilesSize = null,
        disableNext,
        isFileMaxSizeExceeded,
        selectedFilesLocation
    } = props;
    const { isXLargeOrGreater } = useResponsiveMode();
    const [isExpanded, setIsExpanded] = React.useState(false);
    const { localize } = useLocalization();

    const stringifiedFilesNumberQuota = filesNumberQuota?.toLocaleString() ?? 'XXXX';
    const stringifiedFilesSizeQuota = filesSizeQuota?.toLocaleString() ?? 'XXXX';
    const stringifiedSelectedFilesNumber = selectedFilesNumber?.toLocaleString() ?? 'XX';
    const stringifiedSelectedFilesSize = selectedFilesSize?.toLocaleString() ?? 'XX';
    const totalRemainingFilesNumberQuota = filesNumberQuota ? filesNumberQuota - selectedFilesNumber : selectedFilesNumber;
    const totalRemainingFilesSizeQuota = filesSizeQuota ? filesSizeQuota - selectedFilesSize : selectedFilesSize;
    const stringifiedTotalRemainingFilesNumberQuota = totalRemainingFilesNumberQuota.toLocaleString() ?? 'XX';
    const stringifiedTotalRemainingFilesSizeQuota = totalRemainingFilesSizeQuota.toLocaleString() ?? 'XX';

    let messageBarType;
    const getMessageBarText = () => {
        if (selectedFilesNumber >= filesNumberQuota) {
            messageBarType = MessageBarType.error;
            disableNext(true);

            return localize(messages.outOfFilesNumberQuota, {
                remainingFilesNumber: Math.abs(filesNumberQuota - selectedFilesNumber).toLocaleString()
            });
        }
        if (selectedFilesSize >= filesSizeQuota) {
            messageBarType = MessageBarType.error;
            disableNext(true);

            return localize(messages.outOfFilesSizeQuota, {
                remainingFilesSize: Math.abs(filesSizeQuota - selectedFilesSize).toLocaleString()
            });
        }
        if (isFileMaxSizeExceeded) {
            messageBarType = MessageBarType.error;
            disableNext(true);

            return localize(messages.fileExceedMaxSize);
        }
        if (selectedFilesNumber < 0) {
            messageBarType = MessageBarType.error;

            disableNext(true);
            return localize(messages.outOfFilesNumberQuota, {
                remainingFilesNumber: stringifiedTotalRemainingFilesNumberQuota
            });
        }
        if (selectedFilesSize < 0) {
            messageBarType = MessageBarType.error;
            disableNext(true);

            return localize(messages.outOfFilesSizeQuota, {
                remainingFilesSize: stringifiedTotalRemainingFilesSizeQuota
            });
        }
        if (totalRemainingFilesNumberQuota < 0 || totalRemainingFilesSizeQuota < 0) {
            messageBarType = MessageBarType.error;
            disableNext(true);

            return localize(messages.outOfFilesSizeQuota, {
                remainingFilesSize: stringifiedTotalRemainingFilesSizeQuota
            });
        }
        if (selectedFilesNumber / filesNumberQuota >= 0.8) {
            messageBarType = MessageBarType.warning;
            disableNext(false);

            return localize(messages.almostOutOfFilesNumberQuota);
        }
        if (selectedFilesSize / filesSizeQuota >= 0.8) {
            messageBarType = MessageBarType.warning;
            disableNext(false);

            return localize(messages.almostOutOfFilesSizeQuota);
        }

        return (
            <LuisText>
                {localize(messages.title, {
                    selectedFilesNumber: stringifiedSelectedFilesNumber,
                    filesNumberQuota: stringifiedFilesNumberQuota,
                    selectedFilesSize: stringifiedSelectedFilesSize,
                    filesSizeQuota: stringifiedFilesSizeQuota
                })}
            </LuisText>
        );
    };

    disableNext(false);
    const messageBarText = getMessageBarText();
    const CardHeader = (
        <MessageBar messageBarType={messageBarType} styles={{ innerText: { width: '100%' } }}>
            {messageBarText}
        </MessageBar>
    );

    return (
        <ExpandableCard
            isExpanded={isExpanded}
            onRenderHeader={CardHeader}
            isSeparatorDisplayed={false}
            onExpansionChange={() => {
                logDTButtonClick(
                    'document translation',
                    'jobCreation',
                    selectedFilesLocation === 'blobStorage' ? 'blobStorage' : 'uploadFiles',
                    `messagebar:${!isExpanded ? 'expand' : 'collapse'}`,
                    'filesSelection'
                );
                setIsExpanded(!isExpanded);
            }}
            cardName={localize(messages.cardName)}
            styles={expandableCardStyles}>
            <Stack
                horizontal={isXLargeOrGreater}
                tokens={{ childrenGap: isXLargeOrGreater ? 33 : 16, padding: '0 24px 0 0' }}
                styles={{ root: { margin: '0 0 10px 24px' } }}>
                <Stack.Item styles={{ root: { flex: '3' } }}>
                    <ProgressIndicator
                        label={localize(messages.selectedFilesNumberQuotaMessage)}
                        description={localize(messages.selectedQuotaDetailsMessage, {
                            selectedQuota: stringifiedSelectedFilesNumber,
                            totalQuota: stringifiedFilesNumberQuota
                        })}
                        percentComplete={selectedFilesNumber / filesNumberQuota}
                    />
                </Stack.Item>

                <Stack.Item styles={{ root: { flex: '3' } }}>
                    <ProgressIndicator
                        label={localize(messages.selectedFilesSizeQuotaMessage)}
                        description={localize(messages.selectedFilesSizeQuotaDetailsMessage, {
                            selectedFilesSize: stringifiedSelectedFilesSize,
                            filesSizeQuota: stringifiedFilesSizeQuota
                        })}
                        percentComplete={selectedFilesSize / filesSizeQuota}
                    />
                </Stack.Item>

                <Stack.Item styles={{ root: { paddingTop: '4px', flex: 2 } }}>
                    <LuisText>
                        {localize(messages.quotaMessage, {
                            filesNumberQuota: stringifiedFilesNumberQuota,
                            filesSizeQuota: stringifiedFilesSizeQuota,
                            maxFileSize: LuisConstants.TranslationTool.MaxFileSizeMB
                        })}
                    </LuisText>
                </Stack.Item>
            </Stack>
        </ExpandableCard>
    );
});
